import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, RotateCcw, Star } from "lucide-react";
import { sfx } from "../../lib/audio";
import { recordAttempt, say, useRoom } from "../../lib/zhuyinRoom";
import { RoomShell, ZhuyinCard, groupOf } from "../../components/zhuyin/RoomUI";

const ROUND = 10;
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

// Weak symbols come up more often.
const makeRound = (cards) => {
  const weight = (z) => (z.status === "mastered" ? 1 : z.status === "needs_practice" ? 4 : 3);
  const pool = cards.flatMap((z) => Array(weight(z)).fill(z));
  const picked = [];
  while (picked.length < Math.min(ROUND, cards.length)) {
    const z = pool[Math.floor(Math.random() * pool.length)];
    if (!picked.includes(z)) picked.push(z);
  }
  return picked.map((z) => {
    const same = shuffle(cards.filter((c) => c !== z && c.category === z.category));
    const other = shuffle(cards.filter((c) => c !== z && c.category !== z.category));
    return { z, options: shuffle([z, ...[...same, ...other].slice(0, 3)]) };
  });
};

export default function ZhuyinQuiz() {
  const { cards, childId, reload } = useRoom();
  const [qs, setQs] = useState(null);
  const [n, setN] = useState(0);
  const [score, setScore] = useState(0);
  const [wrong, setWrong] = useState([]);
  const [right, setRight] = useState(false);
  const start = useCallback(() => { setQs(makeRound(cards)); setN(0); setScore(0); setWrong([]); setRight(false); }, [cards]);
  useEffect(() => { if (cards && !qs) start(); }, [cards, qs, start]);
  const q = qs?.[n];
  useEffect(() => { if (q) setTimeout(() => say(q.z), 300); }, [q]);

  const pick = (o) => {
    if (right) return;
    const first = wrong.length === 0;
    if (o === q.z) {
      sfx("good");
      setRight(true);
      if (first) setScore((s) => s + 1);
      recordAttempt(childId, q.z.symbol, "room_listen", first);
      setTimeout(() => say(q.z), 400);
    } else {
      sfx("bad");
      setWrong((w) => [...w, o.symbol]);
      setTimeout(() => say(q.z), 500);
    }
  };
  const next = () => { setWrong([]); setRight(false); setN(n + 1); if (n + 1 >= qs.length) reload(); };

  if (qs && n >= qs.length) {
    return (
      <RoomShell title="聽音選注音" testid="zhuyin-quiz-page">
        <div className="flex flex-col items-center gap-6 py-16" data-testid="quiz-result">
          <div className="flex gap-1">{qs.map((_, k) => <Star key={k} size={36} className={k < score ? "text-amber-400" : "text-stone-300"} fill="currentColor" />)}</div>
          <h2 className="font-brush text-5xl text-[var(--vermilion)]" data-testid="quiz-score">答對 {score} / {qs.length} 題</h2>
          <button data-testid="quiz-restart-btn" onClick={start} className="kid-btn px-10 bg-[#1D6FB8] text-white text-2xl font-round flex items-center gap-2"><RotateCcw /> 再玩一次</button>
        </div>
      </RoomShell>
    );
  }
  return (
    <RoomShell title="聽音選注音" testid="zhuyin-quiz-page" right={qs && <span className="font-round text-xl text-[#14507f]" data-testid="quiz-count">第 {n + 1} / {qs.length} 題</span>}>
      {q && (
        <div className="flex flex-col items-center gap-8">
          <motion.button key={n} data-testid="quiz-listen-btn" onClick={() => say(q.z)} initial={{ scale: 0.6 }} animate={{ scale: 1 }}
            className="w-32 h-32 rounded-full bg-[#1D6FB8] text-white flex flex-col items-center justify-center shadow-xl"><Volume2 size={48} /><span className="font-round text-lg">再聽一次</span></motion.button>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {q.options.map((o) => {
              const bad = wrong.includes(o.symbol);
              const good = right && o === q.z;
              return (
                <motion.button key={o.symbol} data-testid={`quiz-option-${o.symbol}`} onClick={() => pick(o)} whileTap={{ scale: 0.9 }} disabled={bad}
                  className={`w-32 h-32 sm:w-36 sm:h-36 rounded-[2rem] text-7xl font-black border-4 shadow-md bg-white ${bad ? "opacity-30 shake" : ""} ${good ? "border-green-500 bg-green-50" : "border-[#b9dcf0]"}`}
                  style={{ color: groupOf(o).color }}>{o.symbol}</motion.button>
              );
            })}
          </div>
          {right && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-4" data-testid="quiz-feedback">
              <ZhuyinCard z={q.z} className="w-56" />
              <button data-testid="quiz-next-btn" onClick={next} className="kid-btn px-10 bg-[var(--jade)] text-white text-2xl font-round">{n + 1 < qs.length ? "下一題" : "看成績"}</button>
            </motion.div>
          )}
        </div>
      )}
    </RoomShell>
  );
}
